import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import FolderListCard from '@components/FolderListCard';
import { toastSuccess, toastWarning } from '@components/Toast';

import useDebounce from '@hooks/useDebounce';

import useCreateFolder from '@features/favourites/hooks/useCreateFolder';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getBrands } from '@store/design/action';

import { setSystemUserPreferences } from '@services/commonService';
import { changeFolder, getFavouriteFolders } from '@services/favouriteService';

import { getUserPreference, printApiError } from '@utils/Util';

import { useGetStyleExistingFoldersQuery } from '../api/favouritesApi';

const SelectFolderPopup = ({
	onClose,
	isShowCreateNew,
	setIsShowCreateNew,
	title = 'Add to favourite',
	currentLikedStyle,
	handleCopyToFolder,
	favFrom = ''
}) => {
	const {
		selectedValue,
		setSelectedValue,
		brands,
		errors,
		handleSubmit: handleCreateFolder
	} = useCreateFolder({ folderName: '', brand: null });

	const userPreference = getUserPreference();
	const folderPopupRef = useRef(null);
	const [selectedFolder, setSelectedFolder] = useState(userPreference?.DEFAULT_FAVOURITE_FOLDER);
	const [defaultFolder, setDefaultFolder] = useState(userPreference?.DEFAULT_FAVOURITE_FOLDER?.id || -1);
	const [folderList, setFolderList] = useState([]);
	const [searchKey, setSearchKey] = useState('');
	const [isShowFolderList, setIsShowFolderList] = useState(false);
	const dispatch = useDispatch();
	const { searchResult } = useSelector((store) => store.collectionSearchOptionStore);

	const {
		data: styleExistingFoldersData,
		refetch,
		isSuccess
	} = useGetStyleExistingFoldersQuery(currentLikedStyle, {
		skip: !currentLikedStyle || favFrom === 'SCAN'
	});

	const fetchFavouriteFolders = useCallback(
		async (searchInput, selectedFolderId) => {
			try {
				dispatch(openLoader());
				const params = `?page=0&size=1000&search=${searchInput}`;
				const response = await getFavouriteFolders(params);
				const existingFolders = styleExistingFoldersData?.map((folder) => folder.id);
				const findSelectedFolder = response.find(
					(folder) => parseInt(folder.id) === parseInt(selectedFolderId)
				);
				if (favFrom !== 'SCAN' && favFrom !== 'FAVOURITE') {
					const newFolderList = response.map((folder) => ({
						...folder,
						hasLikedStyle: existingFolders?.includes(folder.id)
					}));
					const sortedFolderList = [...newFolderList].sort((a, b) => {
						if (a.hasLikedStyle && !b.hasLikedStyle) return -1;
						if (!a.hasLikedStyle && b.hasLikedStyle) return 1;
						return a.name.localeCompare(b.name);
					});
					setFolderList(sortedFolderList);

					if (findSelectedFolder) {
						setSelectedFolder({ ...findSelectedFolder, hasLikedStyle: true });
					}
				} else {
					const sortedFolderList = [...response].sort((a, b) => {
						return a.name.localeCompare(b.name);
					});
					setFolderList(sortedFolderList);
					if (findSelectedFolder) {
						setSelectedFolder(findSelectedFolder);
					}
				}
			} catch (error) {
				printApiError(error);
			} finally {
				dispatch(closeLoader());
			}
		},
		[dispatch, styleExistingFoldersData]
	);

	const handleSuccess = (selectedFolder) => {
		handleUserPreferenceUpdate(selectedFolder);
		fetchFavouriteFolders('', selectedFolder?.id);
		setIsShowFolderList(false);
		setSearchKey('');
		toastSuccess(`Added to favourite`);
	};

	const handleChangeFolder = async (selectedFolder) => {
		try {
			dispatch(openLoader());
			const productIds = Array.isArray(currentLikedStyle) ? currentLikedStyle : [currentLikedStyle];
			const responses = await Promise.all(
				productIds.map((productId) => changeFolder(productId, selectedFolder?.id, defaultFolder))
			);
			if (favFrom !== 'SCAN') {
				refetch();
			}
			if (responses.length > 0 && responses.every((response) => response?.success)) {
				handleSuccess(selectedFolder);
			}
		} catch (error) {
			if (error.response?.status === 400) {
				toastWarning(error.response?.data?.message);
			} else {
				printApiError(error);
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const debouncedSearchKey = useDebounce(searchKey, 300);

	useEffect(() => {
		if (isSuccess || currentLikedStyle || handleCopyToFolder) {
			fetchFavouriteFolders(debouncedSearchKey, defaultFolder);
		}
	}, [
		debouncedSearchKey,
		currentLikedStyle,
		searchResult,
		isSuccess,
		fetchFavouriteFolders,
		defaultFolder,
		refetch
	]);

	const isCreateNew = () => {
		return folderList?.length === 0 && searchKey?.length > 0;
	};

	useEffect(() => {
		setSelectedValue((prev) => ({ ...prev, folderName: searchKey }));
	}, [searchKey]);

	const handleSubmit = async () => {
		try {
			await handleCreateFolder((response) => {
				handleSelectFolder(response);
				fetchFavouriteFolders('', defaultFolder);
				setIsShowCreateNew(false);
				setIsShowFolderList(false);
				setSearchKey('');
			});
		} catch (error) {
			printApiError(error);
		}
	};

	const handleReset = () => {
		setSearchKey('');
		setSelectedValue({ folderName: '', brand: null });
		if (setIsShowCreateNew) setIsShowCreateNew(false);
		setIsShowFolderList(false);
		onClose();
	};

	const handleUserPreferenceUpdate = async (selected) => {
		setDefaultFolder(selected?.id);
		const userPreference = await getUserPreference();
		const body = {
			...userPreference,
			DEFAULT_FAVOURITE_FOLDER: selected
		};
		await setSystemUserPreferences(body);
	};

	const handleSelectFolder = async (selected) => {
		// Styles were just liked into the default folder, so move them instead of copying
		const isMoveLikedStyle = currentLikedStyle && (!handleCopyToFolder || favFrom === 'SCAN');
		if (isMoveLikedStyle) {
			handleChangeFolder(selected);
			setSearchKey('');
		} else {
			handleCopyToFolder && (await handleCopyToFolder(selected, handleSuccess));
			setSearchKey('');
		}
	};

	return (
		<div className="added-to-favorite-popup sample-favourite-popup shadow-12dp" ref={folderPopupRef}>
			<div className="header d-flex justify-content-between align-items-center">
				<p className="regular-14 gray_dark_01">{title}</p>
				<CommonImage
					className="cursor-pointer"
					src="/icons/close.svg"
					width="24"
					alt=""
					onClick={handleReset}
				/>
			</div>

			<div className="folder-and-brand mt-12">
				<div className="header">
					<div className="form-group mb-12">
						<div className="select-component">
							<div className="input-tag">
								<input
									type="text"
									autoComplete="off"
									placeholder="Select or create new folder"
									value={searchKey}
									onChange={(e) => setSearchKey(e.target.value)}
									onClick={() => setIsShowFolderList(true)}
								/>
								<img className="cursor-pointer" src="/images/chevron_down.svg" alt="" />
							</div>
						</div>
						{errors?.folderName && <p className="error">{errors.folderName}</p>}
					</div>
					{isCreateNew() && !isShowCreateNew && (
						<div
							className="new-create py-2 px-3 mt-12 mb-12 cursor-pointer"
							onClick={() => setIsShowCreateNew(true)}
						>
							<p className="regular-14 gray_dark_01 weight-600">
								Create new: <span className="weight-400">{searchKey}</span>
							</p>
						</div>
					)}

					{!isShowCreateNew && (
						<>
							<p className="regular-12 weight-600 gray_dark_02 mt-12">
								Your style added to this folder
							</p>
							<div
								className={`all-select-moodboard scroll-y-label ${
									!isShowFolderList ? 'single-moodbord-sidber' : ''
								}`}
							>
								{!isShowFolderList ? (
									<FolderListCard
										key={selectedFolder?.id}
										folder={selectedFolder}
										onSelect={handleSelectFolder}
										isActive={true}
									/>
								) : (
									folderList?.map((folder) => (
										<FolderListCard
											key={folder?.id}
											folder={folder}
											onSelect={handleSelectFolder}
											isActive={selectedFolder?.id === folder?.id}
										/>
									))
								)}
							</div>
						</>
					)}
					{isShowCreateNew && (
						<>
							<div className="form-group">
								<CustomDropdown
									title="Select Brand"
									onItemSearch={(value) => {
										dispatch(getBrands(`?page=0&size=1000&name=${value}`));
									}}
									items={brands}
									selectedItem={selectedValue.brand?.name}
									onItemClick={(value) =>
										setSelectedValue((prev) => ({ ...prev, brand: value }))
									}
								/>
								{errors?.brand && <p className="error">{errors.brand}</p>}
							</div>
							<div className="form-group mb-3">
								<label htmlFor="style-title">Description</label>
								<textarea
									placeholder="Write description"
									name="style-title"
									id="style-title"
									value={selectedValue.description}
									onChange={(e) =>
										setSelectedValue({
											...selectedValue,
											description: e.target.value
										})
									}
								/>
							</div>
						</>
					)}
				</div>

				{isShowCreateNew && (
					<div className="footer">
						<div className="footer-buttons text-right">
							<button className="button gray-outline size32" onClick={() => handleReset()}>
								Cancel
							</button>
							<button className="button size32 ml-2" onClick={handleSubmit}>
								Submit
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default SelectFolderPopup;
